import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
  Button, 
  TextField,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Box
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import { countryList } from '../../components/utils/countryCodeList';

import { 
  CreateDBEWaitlistEntry 
} from '../../redux/actions/dbeWaitlistActions';

import "./styles.scss";


const Data = {
  name: '',
  company: '',
  email: '',
  phone: '',
  dbe_status: false
};

const DBEWaitlistPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(Data);

  const [countryCode, setCountryCode] = useState('1'); // Default to US
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {

      const submitData = {
        ...formData,
        phone: formData.phone,
        country_code: countryCode
      };
      
      await dispatch(CreateDBEWaitlistEntry(submitData, setLoading));
      
      // Reset form and show success message
      setFormData(Data);
      setCountryCode('1'); // Reset to US
      setSubmitted(true);
    } catch (error) {
      console.error('DBE Waitlist submission failed:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dbe-waitlist">
      <div className="dbe-waitlist__container">
        <div className="dbe-waitlist__hero">
          <h1 className="dbe-waitlist__title">Protect Your DBE Certification</h1>
          <p className="dbe-waitlist__subtitle">
            Join the "Virginia Safety Net" Pilot. Free access to the "TurboTax for DBE Narratives" tool.
          </p>
        </div>

        {!submitted && (
          <div className="dbe-waitlist__alert">
            <div className="dbe-waitlist__alert-icon">⚠️</div>
            <div className="dbe-waitlist__alert-content">
              <span className="dbe-waitlist__alert-title">New USDOT Rule Alert</span>
              <p className="dbe-waitlist__alert-text">
                Effective Jan 3, 2026: Every DBE must submit a "Social Disadvantage Narrative" or risk decertification. Don't lose your status due to paperwork.
              </p>
            </div>
          </div>
        )}

        {submitted ? (
          <div className="dbe-waitlist__success-screen">
            <Box className="dbe-waitlist__success-content">
              <CheckCircleOutlineIcon className="dbe-waitlist__success-icon" />
              
              <Typography variant="h4" className="dbe-waitlist__success-title">
                Welcome to the Pilot!
              </Typography>
              
              <Typography variant="body1" className="dbe-waitlist__success-message">
                You've been successfully added to the Virginia Safety Net waitlist. 
                Our team will review your application and reach out to you soon with the next steps.
              </Typography>
              
              <Typography variant="body2" className="dbe-waitlist__success-submessage">
                In the meantime, feel free to explore our other services and solutions.
              </Typography>
              
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/')}
                className="dbe-waitlist__success-button"
              >
                Return to Home
              </Button>
            </Box>
          </div>
        ) : (
          <div className="dbe-waitlist-form">
          <form onSubmit={handleSubmit}>
            <TextField
              label="Full Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Jane Doe"
              variant="outlined"
              fullWidth
              required
              disabled={loading}
            />

            <TextField
              label="Company Name"
              name="company"
              value={formData.company}
              onChange={handleInputChange}
              placeholder="Acme Construction LLC"
              variant="outlined"
              fullWidth
              required
              disabled={loading}
            />

            <TextField
              label="Email Address"
              name="email"a
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="jane@acme.com"
              variant="outlined"
              fullWidth
              required
              disabled={loading}
            />

            <div className="dbe-waitlist-form__phone-group">
              <FormControl variant="outlined" className="dbe-waitlist-form__country-code">
                <InputLabel id="country-code-label">Code</InputLabel>
                <Select
                  labelId="country-code-label"
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  label="Country code"
                  disabled={loading}
                  renderValue={(value) => `+${value}`}
                >
                  {countryList.map((country) => (
                    <MenuItem key={country.code} value={country.dial_code}>
                      {country.name} (+{country.dial_code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Phone Number"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="5551234567"
                variant="outlined"
                disabled={loading}
                className="dbe-waitlist-form__phone-input"
              />
            </div>

            <FormControlLabel
              control={
                <Checkbox
                  name="dbe_status"
                  checked={formData.dbe_status}
                  onChange={handleInputChange}
                  disabled={loading}
                  sx={{
                    color: '#253f83',
                    '&.Mui-checked': {
                      color: '#0d67e3',
                    },
                  }}
                />
              }
              label="I am a currently certified DBE/SWaM firm."
              className="dbe-waitlist-form__checkbox"
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              className="dbe-waitlist-form__submit"
            >
              {loading ? (
                <>
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                  Submitting...
                </>
              ) : (
                'Join the Pilot Waitlist'
              )}
            </Button>
          </form>
        </div>
        )}

        <div className="dbe-waitlist__footer">
          <p className="dbe-waitlist__footer-subtitle">Virginia SBSD Pilot Initiative</p>
        </div>
      </div>
    </div>
  );
};

export default DBEWaitlistPage;